/**
 * ReservaServicioModal — GESTOR DE VENTA FISIO (servicios del canal cliente).
 *
 * Componente INDEPENDIENTE para el Canal Cliente. El agente Canal-cliente solo
 * tiene que: (1) montar un botón que abra este modal (onReservarFisio), y
 * (2) pasarle `user`. No hace falta tocar VisionClienteTab más que para el botón.
 *
 * Flujo: servicio -> profesional (filtrado por el servicio) -> día (L-J, 2 sem)
 *        -> huecos reales del CRM -> reservar (queda "pendiente de pago").
 * Servicios de médico (solo_mensaje) -> no reserva online: caja de mensaje al editor.
 *
 * El pago por TPV NO va aquí (fase posterior, gated). La reserva escribe SOLO en
 * db.canal_reservas (capa aislada, no contamina el CRM).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import i18n from '../../i18n';

const API = '/api/canal-reservas';
const TEL = '93 414 28 60';
// Días por idioma (namespace canal): largos para "Lunes 13/07/2026" y cortos para los chips.
const DIAS = () => i18n.t('canal:reserva.dias', { returnObjects: true });
const DIAS_CORTOS = () => i18n.t('canal:reserva.diasCortos', { returnObjects: true });

// Próximos días Lunes–Jueves (3 semanas) como {value, dia, dm, semana}
function diasLaborables() {
  const out = [];
  const hoy = new Date();
  const weeks = {};
  // Empezamos en mañana (i=1): el día en curso no se ofrece para reservar.
  for (let i = 1; i < 28 && out.length < 12; i++) {
    const d = new Date(hoy);
    d.setDate(hoy.getDate() + i);
    const wd = (d.getDay() + 6) % 7; // 0=Lunes
    if (wd <= 3) {
      // lunes de esa semana → agrupar por semana (1,2,3)
      const monday = new Date(d);
      monday.setDate(d.getDate() - wd);
      const wk = monday.toISOString().slice(0, 10);
      if (!(wk in weeks)) weeks[wk] = Object.keys(weeks).length + 1;
      out.push({
        value: d.toISOString().slice(0, 10),
        dia: DIAS_CORTOS()[wd],
        dm: `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`,
        semana: weeks[wk],
      });
    }
  }
  return out;
}

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// "2026-07-13" -> "Lunes 13/07/2026"
function fechaLarga(f) {
  if (!f) return '';
  const [Y, M, D] = f.split('-');
  const wd = (new Date(`${f}T00:00`).getDay() + 6) % 7;
  return `${DIAS()[wd]} ${D}/${M}/${Y}`;
}

export default function ReservaServicioModal({ onClose, user }) {
  const { t } = useTranslation('canal');
  const [servicios, setServicios] = useState([]);
  const [profesionales, setProfesionales] = useState([]);
  const [dias] = useState(diasLaborables());
  const [servicioId, setServicioId] = useState('');
  const [profesionalId, setProfesionalId] = useState('');
  const [fecha, setFecha] = useState('');
  const [disp, setDisp] = useState(null);        // {huecos, ultima_hora, mensaje, profesional_nombre}
  const [slot, setSlot] = useState(null);
  const [slotProfId, setSlotProfId] = useState(null); // terapeuta del hueco elegido (para "Indiferente")
  const [msg, setMsg] = useState('');            // texto del buzón (visita médico)
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [emailEstado, setEmailEstado] = useState('idle'); // idle | enviando | enviado | error
  const servicio = servicios.find((s) => s.id === servicioId);
  // El backend decide "primera vez" y calcula el precio con -20% POR SERVICIO
  // (es_primera_vez / precio_base / precio_final / descuento_pct). El modal solo muestra.
  const esPrimeraVez = !!(servicio?.es_primera_vez && (servicio?.descuento_pct || 0) > 0);
  const profesional = profesionales.find((p) => p.id === profesionalId);
  const soloMensaje = !!(servicio?.solo_mensaje || profesional?.solo_mensaje);

  // Solo horas con ≥10h de antelación (el día en curso ya no se ofrece).
  const MIN_ANTICIP_MS = 10 * 60 * 60 * 1000;
  const huecosDisponibles = (disp?.huecos || []).filter((h) => {
    const t = new Date(`${fecha}T${h.hora_inicio}`);
    return !isNaN(t) && t.getTime() - Date.now() >= MIN_ANTICIP_MS;
  });

  // Cargar servicios al montar (el padre controla si se monta o no)
  useEffect(() => {
    fetch(`${API}/servicios`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => {
        const list = d.servicios || [];
        setServicios(list);
        // Arranque neutro: no autoseleccionamos ningún tratamiento.
      })
      .catch(() => setError(t('reserva.errServicios')));
  }, []);

  // Al cambiar servicio -> cargar profesionales del servicio
  useEffect(() => {
    if (!servicioId) return;
    setDisp(null); setSlot(null); setResultado(null);
    fetch(`${API}/servicios/${servicioId}/profesionales`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => {
        const list = d.profesionales || [];
        setProfesionales(list);
        // Arranque neutro: el terapeuta lo elige el cliente.
        setProfesionalId('');
        if (!fecha) setFecha(dias[0]?.value || '');
      })
      .catch(() => setError(t('reserva.errProfesionales')));
  }, [servicioId]); // eslint-disable-line

  const cargarDisponibilidad = useCallback(() => {
    setSlot(null); setSlotProfId(null); setDisp(null); setResultado(null);
    if (!profesionalId || soloMensaje) return;
    const dur = servicio?.duracion_minutos || 60;
    setCargando(true);

    if (profesionalId === '__any__') {
      // "Indiferente": huecos de TODOS los terapeutas del tratamiento, combinados.
      Promise.all(
        profesionales.map((p) =>
          fetch(`${API}/disponibilidad?profesional_id=${p.id}&fecha=${fecha}&duracion=${dur}`, { headers: authHeaders() })
            .then((r) => r.json())
            .then((d) => (d.huecos || []).map((h) => ({ ...h, profesional_id: p.id, profesional_nombre: p.nombre })))
            .catch(() => [])
        )
      )
        .then((arrs) => {
          const merged = [].concat(...arrs).sort((a, b) => a.hora_inicio.localeCompare(b.hora_inicio));
          setDisp({ huecos: merged, _any: true });
        })
        .catch(() => setError(t('reserva.errDisp')))
        .finally(() => setCargando(false));
      return;
    }

    fetch(`${API}/disponibilidad?profesional_id=${profesionalId}&fecha=${fecha}&duracion=${dur}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => setDisp({ ...d, huecos: (d.huecos || []).map((h) => ({ ...h, profesional_id: profesionalId, profesional_nombre: d.profesional_nombre })) }))
      .catch(() => setError(t('reserva.errDisp')))
      .finally(() => setCargando(false));
  }, [profesionalId, fecha, servicio, soloMensaje, profesionales]);

  useEffect(() => { cargarDisponibilidad(); }, [cargarDisponibilidad]);

  const reservar = () => {
    setError(null);
    fetch(`${API}/reservar`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ profesional_id: slotProfId || profesionalId, tratamiento_id: servicioId, fecha, hora_inicio: slot }),
    })
      .then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(j.detail || 'Error'); return j; })
      .then((j) => { setResultado(j.reserva); })
      .catch((e) => setError(e.message));
  };

  const enviarEmail = () => {
    if (!resultado?.id || emailEstado === 'enviando' || emailEstado === 'enviado') return;
    setEmailEstado('enviando');
    fetch(`${API}/reserva/${resultado.id}/email`, { method: 'POST', headers: authHeaders() })
      .then(async (r) => { if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || 'No disponible'); } })
      .then(() => setEmailEstado('enviado'))
      .catch(() => setEmailEstado('error'));
  };

  const imprimir = () => {
    if (!resultado) return;
    const trat = servicio?.nombre || resultado.tratamiento_nombre || '';
    const fisio = (resultado.profesional_nombre || '').split(' ')[0];
    const w = window.open('', '_blank', 'width=520,height=680');
    if (!w) return;
    w.document.write(`<!doctype html><html lang="${i18n.language || 'es'}"><head><meta charset="utf-8"><title>${t('reserva.printTitle')}</title>
      <style>
        body{font-family:'Poppins',system-ui,sans-serif;color:#1f2d2a;padding:28px;max-width:460px;margin:0 auto}
        h1{font-size:20px;color:#2c6b76;text-align:center;margin:0 0 4px}
        .sub{text-align:center;color:#6b7d78;font-size:14px;margin:0 0 16px}
        .trat{text-align:center;font-size:18px;font-weight:800;margin:0 0 16px}
        table{width:100%;border-collapse:collapse;background:#f6faf9;border:1px solid #e6ebe9;border-radius:12px;overflow:hidden}
        td{padding:9px 14px;font-size:14px;border-bottom:1px solid #eef1f0;vertical-align:top}
        tr:last-child td{border-bottom:0}
        td.k{color:#6b7d78;font-weight:600;width:120px}
        .gracias{text-align:center;font-weight:800;color:#2c6b76;margin-top:18px;font-size:16px}
      </style></head><body>
      <h1>${t('reserva.confirmada')}</h1>
      <p class="sub">${t('reserva.detalles')}</p>
      <p class="trat">${trat}</p>
      <table>
        <tr><td class="k">${t('reserva.dia')}</td><td>${fechaLarga(resultado.fecha)}</td></tr>
        <tr><td class="k">${t('reserva.hora')}</td><td>${resultado.hora_inicio}h</td></tr>
        <tr><td class="k">${t('reserva.fisioterapeuta')}</td><td>${fisio}</td></tr>
        <tr><td class="k">${t('reserva.lugar')}</td><td>Instituto de Medicina Física y Rehabilitación MEFIR<br>Calle Madrazo, 33 1-1<br>08006 Barcelona<br>Tel. 93 414 28 60</td></tr>
      </table>
      <p class="gracias">${t('reserva.gracias')}</p>
      </body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  const enviarMensaje = () => {
    setError(null);
    fetch(`${API}/mensaje`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ tratamiento_id: servicioId, profesional_id: profesionalId, texto: msg }),
    })
      .then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(j.detail || 'Error'); return j; })
      .then(() => setResultado({ _mensaje: true }))
      .catch((e) => setError(e.message));
  };

  return (
    <div className="rsm-ov" onClick={(e) => { if (e.target.classList.contains('rsm-ov')) onClose(); }}>
      <style>{CSS}</style>
      <div className="rsm-sheet">
        <button className="rsm-close" onClick={onClose} aria-label={t('reserva.cerrar')}>×</button>
        <h2 className="rsm-title">{resultado && !resultado._mensaje ? t('reserva.confirmada') : t('reserva.titulo')}</h2>

        {resultado?._mensaje ? (
          <div className="rsm-ok">{t('reserva.msgEnviado')}</div>
        ) : resultado ? (
          <div className="rsm-conf">
            <div className="rsm-conf-check">✅</div>
            <p className="rsm-conf-sub">{t('reserva.detalles')}</p>
            <div className="rsm-conf-trat">{servicio?.nombre || resultado.tratamiento_nombre}</div>
            <dl className="rsm-conf-list">
              <div><dt>{t('reserva.dia')}</dt><dd>{fechaLarga(resultado.fecha)}</dd></div>
              <div><dt>{t('reserva.hora')}</dt><dd>{resultado.hora_inicio}h</dd></div>
              <div><dt>{t('reserva.fisioterapeuta')}</dt><dd>{(resultado.profesional_nombre || '').split(' ')[0]}</dd></div>
              <div><dt>{t('reserva.lugar')}</dt><dd>
                Instituto de Medicina Física y Rehabilitación MEFIR<br />
                Calle Madrazo, 33 1-1<br />
                08006 Barcelona<br />
                Tel. 93 414 28 60
              </dd></div>
            </dl>
            <p className="rsm-conf-gracias">{t('reserva.gracias')}</p>
            <div className="rsm-conf-acc">
              <button className="rsm-print" onClick={imprimir}>{t('reserva.imprimir')}</button>
              <button
                className={`rsm-print${emailEstado === 'enviado' ? ' ok' : ''}`}
                onClick={enviarEmail}
                disabled={emailEstado === 'enviando' || emailEstado === 'enviado'}
              >
                {emailEstado === 'enviando' ? t('reserva.enviando') : emailEstado === 'enviado' ? t('reserva.enviado') : t('reserva.enviarEmail')}
              </button>
            </div>
            {emailEstado === 'error' && <p className="rsm-conf-emailerr">{t('reserva.emailErr')}</p>}
          </div>
        ) : (
          <>
            <label className="rsm-lbl">{t('reserva.tratamiento')}</label>
            <select className="rsm-in" value={servicioId} onChange={(e) => setServicioId(e.target.value)}>
              <option value="">{t('reserva.selTratamiento')}</option>
              {servicios.map((s) => (
                <option key={s.id} value={s.id}>{s.nombre}</option>
              ))}
            </select>
            {servicio?.precio != null && (esPrimeraVez ? (
              <div className="rsm-precio rsm-precio-primera">
                <div className="rsm-precio-msg"><Trans t={t} i18nKey="reserva.primeraVez" components={{ b: <b /> }} /></div>
                <div className="rsm-precio-row">
                  <span>{t('reserva.precioSesion')}</span>
                  <span className="rsm-precio-desc">
                    <s>{(servicio.precio_base ?? servicio.precio)}€</s>
                    <b>{Number(servicio.precio_final ?? servicio.precio).toFixed(2).replace('.', ',')}€</b>
                    <em>−{servicio.descuento_pct}%</em>
                  </span>
                </div>
              </div>
            ) : (
              <div className="rsm-precio">
                <span>{t('reserva.precioSesion')}</span>
                <b>{servicio.precio}€</b>
              </div>
            ))}

            <label className="rsm-lbl">{t('reserva.fisioterapeuta')}</label>
            <select className="rsm-in" value={profesionalId} onChange={(e) => setProfesionalId(e.target.value)} disabled={!servicioId}>
              <option value="">{t('reserva.selFisio')}</option>
              {profesionales.length > 0 && <option value="__any__">{t('reserva.indiferente')}</option>}
              {profesionales.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>

            {soloMensaje ? (
              <div className="rsm-msgbox">
                <p><Trans t={t} i18nKey="reserva.soloMensaje" values={{ tel: TEL }} components={{ tel: <a href={`tel:+34${TEL.replace(/\s/g, '')}`} /> }} /></p>
                <textarea value={msg} onChange={(e) => setMsg(e.target.value)} placeholder={t('reserva.placeholderMsg')} />
                <button className="rsm-cta" disabled={!msg.trim()} onClick={enviarMensaje}>{t('reserva.enviarMensaje')}</button>
                <p className="rsm-muted">{t('reserva.equipoVera')}</p>
              </div>
            ) : (
              <>
                <label className="rsm-lbl">{t('reserva.dia')}</label>
                {[1, 2, 3].map((sem) => {
                  const ds = dias.filter((d) => d.semana === sem);
                  if (!ds.length) return null;
                  return (
                    <div key={sem} className="rsm-weekgrid">
                      {ds.map((d) => (
                        <button
                          key={d.value}
                          className={`rsm-daychip${fecha === d.value ? ' sel' : ''}`}
                          onClick={() => setFecha(d.value)}
                        >
                          <span className="d">{d.dia}</span>
                          <span className="dm">{d.dm}</span>
                        </button>
                      ))}
                    </div>
                  );
                })}

                <label className="rsm-lbl">{disp?._any ? t('reserva.dispAny') : disp?.profesional_nombre ? t('reserva.dispDe', { nombre: disp.profesional_nombre.split(' ').slice(0, 2).join(' ') }) : t('reserva.disponibilidad')}</label>
                <div className="rsm-slotsbox">
                  {cargando ? <span className="rsm-muted">{t('reserva.cargandoDisp')}</span>
                    : huecosDisponibles.length
                      ? (
                          <div className="rsm-slots">
                            {huecosDisponibles.map((h) => (
                              <button
                                key={`${h.profesional_id}-${h.hora_inicio}`}
                                className={`rsm-slot${slot === h.hora_inicio && slotProfId === h.profesional_id ? ' sel' : ''}`}
                                onClick={() => { setSlot(h.hora_inicio); setSlotProfId(h.profesional_id); }}
                              >
                                <span className="rsm-slot-hora">{h.hora_inicio}</span>
                                {disp?._any && h.profesional_nombre && <span className="rsm-slot-prof">{h.profesional_nombre.split(' ')[0]}</span>}
                              </button>
                            ))}
                          </div>
                        )
                      : <span className="rsm-empty">{profesionalId
                          ? t('reserva.sinDisp')
                          : t('reserva.eligeTratamiento')}</span>}
                </div>

                {!cargando && profesionalId && disp && !huecosDisponibles.length && (
                  <p className="rsm-help"><Trans t={t} i18nKey="reserva.noEncuentras" values={{ tel: TEL }} components={{ tel: <a href={`tel:+34${TEL.replace(/\s/g, '')}`} /> }} /></p>
                )}

                <div className="rsm-footer">
                  <button className="rsm-cta" disabled={!slot} onClick={reservar}>{t('reserva.reservar')}</button>
                </div>
              </>
            )}
          </>
        )}

        {error && <div className="rsm-err">{error}</div>}
      </div>
    </div>
  );
}

const CSS = `
.rsm-ov{position:fixed;inset:0;background:rgba(20,41,46,.45);display:flex;align-items:center;justify-content:center;z-index:1000;padding:24px;box-sizing:border-box}
.rsm-sheet{background:#ffffff;color:#1f2d2a;width:100%;max-width:520px;border-radius:22px;padding:18px 18px 22px;max-height:calc(100vh - 40px);overflow:auto;font-family:'Poppins',system-ui,sans-serif}
.rsm-close{float:right;background:none;border:0;color:#6b7d78;font-size:24px;cursor:pointer;line-height:1}
.rsm-title{margin:0 0 10px;font-size:18px;color:#1f2d2a;font-weight:800}
.rsm-lbl{display:block;font-size:13px;font-weight:600;color:#6b7d78;margin:10px 0 4px;text-transform:none;letter-spacing:0}
.rsm-in{width:100%;padding:9px 12px;border-radius:11px;border:1px solid #d7e0dd;background:#ffffff;color:#1f2d2a;font-size:14.5px;box-sizing:border-box;-webkit-appearance:none;appearance:none;background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%236b7d78' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>");background-repeat:no-repeat;background-position:right 12px center}
.rsm-weekgrid{display:grid;grid-template-columns:repeat(4,1fr);gap:6px;margin-bottom:6px}
.rsm-daychip{display:flex;flex-direction:column;align-items:center;gap:1px;padding:6px 4px;border-radius:10px;border:1px solid #d7e0dd;background:#ffffff;color:#1f2d2a;cursor:pointer;font-family:inherit}
.rsm-daychip .d{font:700 10.5px/1 inherit;text-transform:uppercase;letter-spacing:.04em}
.rsm-daychip .dm{font:600 12.5px/1 inherit;color:#6b7d78}
.rsm-daychip.sel{background:#2c6b76;border-color:#2c6b76}
.rsm-daychip.sel .d,.rsm-daychip.sel .dm{color:#fff}
.rsm-slotsbox{margin-top:4px;min-height:74px;border:1px solid #e6ebe9;border-radius:12px;background:#ffffff;padding:10px;display:flex;align-items:center;justify-content:center}
.rsm-slots{display:grid;grid-template-columns:repeat(auto-fill,minmax(84px,1fr));gap:8px;width:100%}
.rsm-slot{display:flex;flex-direction:column;align-items:center;gap:2px;padding:9px 8px;border-radius:11px;border:1px solid #2c6b76;background:#ffffff;color:#2c6b76;cursor:pointer;font-weight:600;text-align:center;font-family:inherit}
.rsm-slot-hora{font-size:15px;font-weight:700;line-height:1}
.rsm-slot-prof{font-size:11px;font-weight:600;opacity:.75;line-height:1}
.rsm-slot.sel{background:#2c6b76;color:#fff}
.rsm-slot.sel .rsm-slot-prof{opacity:.9}
.rsm-empty{color:#6b7d78;font-size:13.5px;text-align:center;line-height:1.45}
.rsm-help{margin:10px 0 0;font-size:13.5px;color:#6b7d78}
.rsm-help a{color:#2c6b76;font-weight:700;text-decoration:none}
.rsm-muted{color:#6b7d78;font-size:13.5px}
.rsm-muted a,.rsm-msgbox a{color:#2c6b76;font-weight:700;text-decoration:none}
.rsm-promo{margin:2px 0 10px;padding:8px 12px;border-radius:11px;background:#eef6f4;border:1px solid #cfe5e0;color:#1f5a52;font-size:13px;line-height:1.35}
.rsm-promo b{color:#2c6b76}
.rsm-precio{margin-top:6px;padding:8px 12px;border-radius:11px;background:#f6faf9;border:1px solid #e6ebe9;display:flex;align-items:center;justify-content:space-between;gap:12px}
.rsm-precio span{font-size:13px;color:#6b7d78}
.rsm-precio b{font-weight:800;font-size:20px;color:#1f2d2a;white-space:nowrap}
.rsm-precio-primera{flex-direction:column;align-items:stretch;gap:8px;background:#eef6f4;border-color:#cfe5e0}
.rsm-precio-msg{font-size:13px;color:#1f5a52;line-height:1.35}
.rsm-precio-msg b{color:#2c6b76}
.rsm-precio-row{display:flex;align-items:center;justify-content:space-between;gap:12px}
.rsm-precio-desc{display:inline-flex;align-items:baseline;gap:8px}
.rsm-precio-desc s{color:#6b7d78;font-size:14px}
.rsm-precio-desc b{font-weight:800;font-size:20px;color:#1f2d2a}
.rsm-precio-desc em{font-style:normal;font-size:12px;font-weight:700;color:#fff;background:#2c6b76;padding:2px 7px;border-radius:999px}
.rsm-pay{margin-top:14px;padding:12px;border-radius:12px;background:#eef6f4;border:1px dashed #2c6b76;color:#2c6b76;font-size:13px}
.rsm-footer{position:sticky;bottom:-22px;margin:12px -18px -22px;padding:10px 18px 18px;background:#ffffff;border-top:1px solid #eef1f0}
.rsm-footer .rsm-pay{margin-top:0}
.rsm-cta{margin-top:0;width:100%;padding:13px;border-radius:13px;border:0;background:#2c6b76;color:#fff;font-weight:700;font-size:15.5px;cursor:pointer}
.rsm-cta:disabled{opacity:.4;cursor:default}
.rsm-msgbox{margin-top:12px;background:#f6faf9;border:1px solid #d7e0dd;border-radius:14px;padding:14px}
.rsm-msgbox p{margin:0 0 10px;font-size:14px;color:#3a4a46}
.rsm-msgbox textarea{width:100%;min-height:88px;box-sizing:border-box;border-radius:12px;border:1px solid #d7e0dd;background:#ffffff;color:#1f2d2a;padding:12px;font:400 14.5px/1.5 inherit;resize:vertical}
.rsm-ok{background:#e4f5ec;color:#1e7a52;padding:14px;border-radius:14px;margin-top:6px}
.rsm-conf{text-align:center;padding:4px 2px 8px}
.rsm-conf-check{font-size:40px;line-height:1;margin:2px 0 6px}
.rsm-conf-sub{margin:0 0 14px;color:#6b7d78;font-size:14px}
.rsm-conf-trat{font-size:18px;font-weight:800;color:#1f2d2a;margin-bottom:14px}
.rsm-conf-list{margin:0;text-align:left;background:#f6faf9;border:1px solid #e6ebe9;border-radius:14px;padding:6px 14px}
.rsm-conf-list>div{display:flex;gap:12px;padding:9px 0;border-bottom:1px solid #eef1f0}
.rsm-conf-list>div:last-child{border-bottom:0}
.rsm-conf-list dt{flex:0 0 108px;color:#6b7d78;font-size:13.5px;font-weight:600}
.rsm-conf-list dd{margin:0;flex:1;color:#1f2d2a;font-size:14px;font-weight:600;line-height:1.5}
.rsm-conf-gracias{margin:16px 0 12px;font-size:16px;font-weight:800;color:#2c6b76}
.rsm-conf-acc{display:flex;gap:10px;justify-content:center;flex-wrap:wrap}
.rsm-print{display:inline-flex;align-items:center;gap:6px;padding:10px 18px;border-radius:11px;border:1px solid #2c6b76;background:#ffffff;color:#2c6b76;font-weight:700;font-size:14px;cursor:pointer;font-family:inherit}
.rsm-print:disabled{opacity:.6;cursor:default}
.rsm-print.ok{background:#e4f5ec;border-color:#1e7a52;color:#1e7a52}
.rsm-conf-emailerr{margin:10px 0 0;font-size:13px;color:#6b7d78}
.rsm-err{background:#fdecec;color:#b3261e;padding:12px;border-radius:12px;margin-top:12px;font-size:14px}
`;
